import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from '../../auth/SupabaseClient';
import { Search, User, Plus, Phone, Mail, Filter, ChevronRight } from 'lucide-react';

interface Client {
  id: number;
  name: string;
  email: string;
  telephone: string;
  date_creation: string;
  tags: string[];
}

const Clients: React.FC = () => {
  const { darkMode } = useOutletContext<{ darkMode: boolean }>();
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('date_creation', { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Une erreur est survenue lors de la récupération des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.telephone.includes(searchTerm)
  );

  const navigateToClientDetails = (clientId: number) => {
    navigate(`/dashboard/clients/${clientId}`);
  };

  const navigateToAddClient = () => {
    navigate('/dashboard/clients/add');
  };

  const renderClientCard = (client: Client) => (
    <div 
      key={client.id} 
      onClick={() => navigateToClientDetails(client.id)}
      className={`cursor-pointer rounded-lg p-4 mb-4 transition-all duration-200 hover:shadow-lg ${
        darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50 border border-gray-200'
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div className={`rounded-full p-3 mr-4 ${
            darkMode ? 'bg-blue-600 bg-opacity-30' : 'bg-blue-100'
          }`}>
            <User size={24} className={darkMode ? 'text-blue-300' : 'text-blue-600'} />
          </div>
          <div>
            <h3 className="font-medium text-lg">{client.name}</h3>
            <div className="flex mt-1 space-x-4">
              {client.email && (
                <div className="flex items-center text-sm text-gray-500">
                  <Mail size={14} className="mr-1" />
                  <span>{client.email}</span>
                </div>
              )}
              {client.telephone && (
                <div className="flex items-center text-sm text-gray-500">
                  <Phone size={14} className="mr-1" />
                  <span>{client.telephone}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center">
          {client.tags && client.tags.length > 0 && (
            <div className="hidden sm:flex mr-4">
              {client.tags.slice(0, 2).map((tag, index) => (
                <span 
                  key={index}
                  className={`px-2 py-1 text-xs rounded-full mr-2 ${
                    darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {tag}
                </span>
              ))}
              {client.tags.length > 2 && (
                <span className={`px-2 py-1 text-xs rounded-full ${
                  darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}>
                  +{client.tags.length - 2}
                </span>
              )}
            </div>
          )}
          <ChevronRight className="text-gray-500" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {/* Header section with search and add button */}
      <div className={`mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between ${
        darkMode ? 'text-white' : 'text-gray-800'
      }`}>
        <div className="relative w-full sm:w-96">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={handleSearch}
            className={`pl-10 pr-4 py-2 w-full rounded-lg ${
              darkMode 
                ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500'
            } border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
          />
        </div>
        <div className="flex space-x-3 w-full sm:w-auto justify-center">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg border ${
              darkMode 
                ? 'bg-gray-800 text-white border-gray-600 hover:bg-gray-700' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
          >
            <Filter size={18} className="mr-2" />
            Filtres
          </button>
          <button 
            onClick={navigateToAddClient}
            className={`flex items-center px-4 py-2 rounded-lg ${
              darkMode 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-blue-500 text-white hover:bg-blue-600'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
          >
            <Plus size={18} className="mr-2" />
            Nouveau Client
          </button>
        </div>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className={`mb-6 p-4 rounded-lg ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <h3 className="text-lg font-medium mb-4">Filtres</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filter inputs would go here */}
            <div className="flex flex-col">
              <label className={`mb-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Tags
              </label>
              <select className={`p-2 rounded-md border ${
                darkMode 
                  ? 'bg-gray-700 text-white border-gray-600' 
                  : 'bg-white text-gray-900 border-gray-300'
              }`}>
                <option value="">Tous les tags</option>
                <option value="vip">VIP</option>
                <option value="prospect">Prospect</option>
                <option value="fidélité">Fidélité</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className={`mb-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Date d'ajout
              </label>
              <select className={`p-2 rounded-md border ${
                darkMode 
                  ? 'bg-gray-700 text-white border-gray-600' 
                  : 'bg-white text-gray-900 border-gray-300'
              }`}>
                <option value="">Toutes les dates</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
                <option value="year">Cette année</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className={`mb-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Trier par
              </label>
              <select className={`p-2 rounded-md border ${
                darkMode 
                  ? 'bg-gray-700 text-white border-gray-600' 
                  : 'bg-white text-gray-900 border-gray-300'
              }`}>
                <option value="newest">Plus récent</option>
                <option value="oldest">Plus ancien</option>
                <option value="name">Alphabétique</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Clients list */}
      <div className={`rounded-lg shadow-sm ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      } p-4`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {searchTerm ? 'Résultats de recherche' : 'Clients'}
            <span className={`ml-2 text-sm font-normal ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              ({filteredClients.length})
            </span>
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center my-12">
            <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${
              darkMode ? 'border-blue-400' : 'border-blue-500'
            }`}></div>
          </div>
        ) : error ? (
          <div className={`text-center py-8 ${darkMode ? 'text-red-400' : 'text-red-500'}`}>
            {error}
          </div>
        ) : filteredClients.length === 0 ? (
          <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {searchTerm ? 'Aucun client ne correspond à votre recherche' : 'Aucun client trouvé'}
          </div>
        ) : (
          <div>
            {filteredClients.map(client => renderClientCard(client))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;